import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const ChatbotTablePagination = ({
  title,
  columns,
  total,
  page,
  limit,
  totalPages,
  data,
  onAdd,
  onView,
  onEdit,
  onDelete,
  btnName,
  handleStatusClick,
  handlePagination,
  searchTerm,
  setSearchTerm
}) => {
  const [currentPage, setCurrentPage] = useState(page || 1);

  useEffect(() => {
    setCurrentPage(page || 1);
  }, [page]);

  // Filter data dynamically
  const filteredData = data.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const startIndex = (currentPage - 1) * limit;
  const currentData = filteredData.slice(0, limit); // API already provides paginated data

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      handlePagination && handlePagination(newPage);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-6 mb-6 mt-5">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2 mb-4 border rounded-lg px-3 py-2 w-1/2">
          <FaSearch size={14} className="text-gray-500" />
          <input
            type="text"
            placeholder={`Search By brand, offerings id , brand id , country ,tier, category ...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none w-full text-sm"
          />
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg shadow bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 transition"
        >
          <FaPlus size={14} /> {btnName}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="p-3 text-sm font-semibold text-gray-700"
                >
                  {col.label}
                </th>
              ))}
              <th className="p-3 text-sm font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row) => (
                <tr
                  key={row.id}
                  className="border-b hover:bg-gray-50 transition"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="p-3 text-sm text-gray-800"
                      style={{ width: col.width }}
                    >
                      {col.key === "status" ? (
                        // ✅ Toggle Switch
                        <div
                          onClick={() => handleStatusClick(row)}
                          className={`relative w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors
          ${row[col.key] === 1 ? "bg-green-500" : "bg-red-500"}`}
                        >
                          <div
                            className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform
            ${row[col.key] === 1 ? "translate-x-6" : "translate-x-0"}`}
                          ></div>
                        </div>
                      ) : col.key === "creative_media" ? (
                        // ✅ Creative Media (Image)
                        <img
                          src={row[col.key]}
                          alt="Creative"
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : col.key === "preference" ? (
                        // ✅ Online / Offline Indicator
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${row[col.key] === "ONLINE" ? "bg-green-500" : "bg-red-500"
                              }`}
                          ></span>
                          <span
                            className={`text-sm font-medium ${row[col.key] === "ONLINE" ? "text-green-600" : "text-red-600"
                              }`}
                          >
                            {row[col.key] === "ONLINE" ? "ONLINE" : "OFFLINE"}
                          </span>
                        </div>
                      ) : col.key === "id" ? (
                        <span
                          className="text-sm text-gray-500 "
                        >
                          {row[col.key]}
                        </span>
                      ) : (
                        // ✅ Default case (truncate to 10 chars)
                        <span className="text-sm text-gray-500">
                          {row[col.key]?.toString().slice(0, 10)}
                        </span>
                      )}
                    </td>
                  ))}

                  <td className="p-3 flex gap-3">
                    <button
                      onClick={() => onView(row)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <FaEye size={14} />
                    </button>
                    <button
                      onClick={() => onEdit(row)}
                      className="text-green-500 hover:text-green-700"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(row)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="p-3 text-center text-gray-500"
                >
                  No {title.toLowerCase()} found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {/* Prev */}
          <button
            className="px-3 py-1 rounded bg-gray-200 text-sm disabled:opacity-50"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FaChevronLeft />
          </button>

          {/* First Page */}
          <button
            onClick={() => handlePageChange(1)}
            className={`px-3 py-1 rounded text-sm ${currentPage === 1
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
              }`}
          >
            1
          </button>

          {/* Left Ellipsis */}
          {currentPage > 3 && <span className="px-2">...</span>}

          {/* Middle Pages */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) =>
                p !== 1 &&
                p !== totalPages &&
                p >= currentPage - 1 &&
                p <= currentPage + 1
            )
            .map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`px-3 py-1 rounded text-sm ${p === currentPage
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                  }`}
              >
                {p}
              </button>
            ))}

          {/* Right Ellipsis */}
          {currentPage < totalPages - 2 && <span className="px-2">...</span>}

          {/* Last Page */}
          {totalPages > 1 && (
            <button
              onClick={() => handlePageChange(totalPages)}
              className={`px-3 py-1 rounded text-sm ${currentPage === totalPages
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
                }`}
            >
              {totalPages}
            </button>
          )}

          {/* Next */}
          <button
            className="px-3 py-1 rounded bg-gray-200 text-sm disabled:opacity-50"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <FaChevronRight />
          </button>
        </div>
      )}


    </div>
  );
};

export default ChatbotTablePagination;
